import moment from "moment"
import { BigNumber, utils } from "ethers"
import Address from "components/Address"
import { ArtBlocksChainId } from "contractConfig"
import { ArtBlocksTransferMetadata } from "utils/artBlocksMetadataApi"
import {
  getBlockExplorerTransactionUrl,
  getBlockExplorerName,
  isArtBlocksChainId,
} from "utils/artBlocksUrls"

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000"

interface Props {
  transfers?: ArtBlocksTransferMetadata[]
  chainId?: number | string | null
  loading?: boolean
  error?: boolean
}

const isZeroAddress = (address?: string | null) =>
  Boolean(address && address.toLowerCase() === ZERO_ADDRESS)

const formatNativeValue = (value?: string) => {
  if (!value) return null

  try {
    const amount = BigNumber.from(value)
    if (amount.isZero()) return null

    const eth = utils.formatEther(amount)
    const numericEth = Number(eth)

    if (!Number.isFinite(numericEth)) return `${eth} ETH`
    if (numericEth > 0 && numericEth < 0.0001) return "<0.0001 ETH"

    return `${numericEth.toLocaleString(undefined, {
      maximumFractionDigits: 4,
      minimumFractionDigits: numericEth >= 1 ? 0 : 4,
    })} ETH`
  } catch {
    return null
  }
}

const getEventLabel = (transfer: ArtBlocksTransferMetadata) => {
  if (isZeroAddress(transfer.from_address)) return "Mint"
  if (isZeroAddress(transfer.to_address)) return "Burn"
  if (formatNativeValue(transfer.native_value)) return "Sale"

  return "Transfer"
}

const ProvenanceAddress = ({ address }: { address: string }) => {
  if (isZeroAddress(address)) {
    return <span className="text-gallery-muted">Origin</span>
  }

  return <Address address={address} />
}

const TokenProvenance = ({ transfers = [], chainId, loading = false, error = false }: Props) => {
  const resolvedChainId = isArtBlocksChainId(chainId) ? Number(chainId) as ArtBlocksChainId : undefined

  return (
    <section className="mb-8">
      <h2 className="gallery-label text-gallery-muted mb-4">Provenance</h2>

      {loading && transfers.length === 0 && (
        <div className="gallery-empty py-6">Loading provenance.</div>
      )}

      {error && transfers.length === 0 && (
        <div className="gallery-empty py-6 text-red-500">Provenance unavailable.</div>
      )}

      {!loading && !error && transfers.length === 0 && (
        <div className="gallery-empty py-6">No provenance events indexed yet.</div>
      )}

      {transfers.length > 0 && (
        <div className="max-h-[32rem] overflow-y-auto border-y border-gallery-border divide-y divide-gallery-border">
          {transfers.map((transfer) => {
            const value = formatNativeValue(transfer.native_value)
            const transactionHref = resolvedChainId
              ? getBlockExplorerTransactionUrl({
                  chainId: resolvedChainId,
                  transactionHash: transfer.transaction_hash,
                })
              : undefined
            const explorerLabel = resolvedChainId ? getBlockExplorerName(resolvedChainId) : undefined

            return (
              <article key={transfer.id} className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="gallery-label text-gallery-text">{getEventLabel(transfer)}</p>
                    <p className="mt-1 text-xs text-gallery-muted">
                      {moment.unix(transfer.block_timestamp).format("MMM D, YYYY")}
                    </p>
                  </div>

                  {transactionHref && (
                    <a
                      href={transactionHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors whitespace-nowrap"
                    >
                      {explorerLabel}
                    </a>
                  )}
                </div>

                <dl className="mt-3 grid grid-cols-[3.5rem_1fr] gap-x-3 gap-y-2 text-sm">
                  <dt className="gallery-label text-gallery-muted">From</dt>
                  <dd className="min-w-0 break-all">
                    <ProvenanceAddress address={transfer.from_address} />
                  </dd>

                  <dt className="gallery-label text-gallery-muted">To</dt>
                  <dd className="min-w-0 break-all">
                    <ProvenanceAddress address={transfer.to_address} />
                  </dd>

                  {value && (
                    <>
                      <dt className="gallery-label text-gallery-muted">Value</dt>
                      <dd className="text-gallery-text">{value}</dd>
                    </>
                  )}
                </dl>
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}

export default TokenProvenance
